import React, {
  useCallback,
  useState
} from "react";
import styled, { css } from "styled-components";

import { Button } from "../Button";
import {
  CapElementSize,
  ImageCropper
} from "./ImageCropper";

interface ImageCropperEditorProps {
  show: boolean;
  src: File;
  onDismiss: () => void;
  onSubmit: (file: File) => void;
  title?: string;
  cropInsetPercentage?: number;
  aspect: number;
  capImageDisplaySize?: CapElementSize
}

// renders an editor to crop a given image
const ImageCropperEditor: React.ReactFCWithChildren<ImageCropperEditorProps> = (({
  aspect,
  show,
  src,
  capImageDisplaySize,
  onDismiss,
  onSubmit
}) => {
  const [ blob, setBlob ] = useState<Blob>();

  // Converts the cropped image Blob to a File before passing it back to the origin
  // via the given onSubmit function
  const handleDoneClick = useCallback(() => {
    if (!blob) {
      return;
    }

    // Convert the Blob to a File and call the given onSubmit function to
    // pass the cropped image File back to the caller
    onSubmit(new File([ blob ], src.name, { type: src.type }));
  }, [
    src,
    blob,
    onSubmit
  ]);

  if (!show) {
    return null;
  }

  return (
    <ImageCropperEditorWrapper capImageDisplaySize={capImageDisplaySize}>
      <div className="cropper-image-wrapper">
        <ImageCropper
          src={src}
          aspect={aspect}
          cropInsetPercentage={0.669} // 66.9%
          onComplete={blob => {
            setBlob(blob);
          }}
        />
      </div>

      <div className="cropper-buttons">
        <Button
          $buttonType="secondary"
          onClick={onDismiss}
        >
          Cancel
        </Button>

        <Button
          $buttonType="primary"
          onClick={handleDoneClick}
        >
          Done
        </Button>
      </div>
    </ImageCropperEditorWrapper>
  );
});

const ImageCropperEditorWrapper = styled.div<{capImageDisplaySize?: CapElementSize}>`
  ${({ theme, capImageDisplaySize }) => css`
    width: ${capImageDisplaySize?.width ? capImageDisplaySize.width + "px" : "inherit"};
    margin-bottom: ${theme.spacing.margins.md};
    
    .cropper-image-wrapper {
      hight: ${capImageDisplaySize?.height ? capImageDisplaySize.height + "px" : "inherit"};
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .cropper-buttons {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    
      margin-top: ${theme.spacing.margins.sm};
      padding: ${theme.spacing.padding.md} ${theme.spacing.padding.lg};
      border-top: 1px solid ${theme.colors.primaryDark};
    }
  `}
`;

export default ImageCropperEditor;
